import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { memo, useMemo, useState } from 'react';
import { RequirementRow } from './RequirementRow';
export const ComplianceMatrixTable = /*#__PURE__*/ memo(function ComplianceMatrixTable({ data, onStatusChange, updatingRequirementId, onRowClick, onAskAI }) {
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [sortField, setSortField] = useState('sequenceNumber');
    const [sortDirection, setSortDirection] = useState('asc');
    const sortedRequirements = useMemo(()=>{
        const sorted = [
            ...data.requirements
        ].sort((a, b)=>{
            let aVal;
            let bVal;
            switch(sortField){
                case 'sequenceNumber':
                    aVal = a.sequenceNumber;
                    bVal = b.sequenceNumber;
                    break;
                case 'category':
                    aVal = a.category;
                    bVal = b.category;
                    break;
                case 'obligationStrength':
                    aVal = a.obligationStrength;
                    bVal = b.obligationStrength;
                    break;
                case 'status':
                    aVal = a.status;
                    bVal = b.status;
                    break;
                default:
                    return 0;
            }
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const comparison = String(aVal).localeCompare(String(bVal));
            return sortDirection === 'asc' ? comparison : -comparison;
        });
        return sorted;
    }, [
        data.requirements,
        sortField,
        sortDirection
    ]);
    const handleSort = (field)=>{
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };
    const handleSelectAll = (e)=>{
        if (e.target.checked) {
            setSelectedRows(new Set(data.requirements.map((r)=>r.requirementId)));
        } else {
            setSelectedRows(new Set());
        }
    };
    const handleRowSelect = (requirementId, checked)=>{
        const newSelected = new Set(selectedRows);
        if (checked) {
            newSelected.add(requirementId);
        } else {
            newSelected.delete(requirementId);
        }
        setSelectedRows(newSelected);
    };
    const allSelected = data.requirements.length > 0 && selectedRows.size === data.requirements.length;
    const someSelected = selectedRows.size > 0 && selectedRows.size < data.requirements.length;
    const SortHeader = ({ field, children, className })=>/*#__PURE__*/ _jsx("th", {
            className: `px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition-colors ${className || ''}`,
            onClick: ()=>handleSort(field),
            children: /*#__PURE__*/ _jsxs("span", {
                className: "flex items-center gap-1",
                children: [
                    children,
                    sortField === field && /*#__PURE__*/ _jsx("span", {
                        className: "text-blue-500",
                        children: sortDirection === 'asc' ? '↑' : '↓'
                    })
                ]
            })
        });
    if (sortedRequirements.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "rounded-lg border border-gray-200 bg-white",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center justify-center py-16 px-4",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3",
                        children: /*#__PURE__*/ _jsxs("svg", {
                            className: "w-6 h-6 text-gray-400",
                            fill: "none",
                            stroke: "currentColor",
                            viewBox: "0 0 24 24",
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: "No requirements"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    strokeWidth: 1.5,
                                    d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-sm font-medium text-gray-900",
                        children: "No requirements found"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-gray-500 mt-1 text-center max-w-sm",
                        children: data.requirements.length === 0 ? 'Process solicitation documents to generate the compliance matrix.' : 'No requirements match your current filters.'
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-lg border border-gray-200 bg-white overflow-hidden",
        "data-testid": Testids.compliance.matrixTable,
        children: [
            selectedRows.size > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-sm font-medium text-blue-700",
                        children: [
                            selectedRows.size,
                            " selected"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setSelectedRows(new Set()),
                        className: "text-xs text-blue-600 hover:text-blue-800",
                        children: "Clear"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("table", {
                className: "w-full",
                children: [
                    /*#__PURE__*/ _jsx("thead", {
                        className: "bg-gray-50/80 border-b border-gray-200",
                        children: /*#__PURE__*/ _jsxs("tr", {
                            children: [
                                /*#__PURE__*/ _jsx("th", {
                                    className: "pl-4 pr-2 py-2.5 w-10",
                                    children: /*#__PURE__*/ _jsx("input", {
                                        type: "checkbox",
                                        checked: allSelected,
                                        ref: (input)=>{
                                            if (input) input.indeterminate = someSelected;
                                        },
                                        onChange: handleSelectAll,
                                        className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SortHeader, {
                                    field: "sequenceNumber",
                                    children: "Requirement"
                                }),
                                /*#__PURE__*/ _jsx(SortHeader, {
                                    field: "category",
                                    className: "w-28",
                                    children: "Type"
                                }),
                                /*#__PURE__*/ _jsx(SortHeader, {
                                    field: "obligationStrength",
                                    className: "w-16 text-center",
                                    children: "Obl."
                                }),
                                /*#__PURE__*/ _jsx(SortHeader, {
                                    field: "status",
                                    className: "w-36",
                                    children: "Status"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("tbody", {
                        children: sortedRequirements.map((requirement)=>/*#__PURE__*/ _jsx(RequirementRow, {
                                requirement: requirement,
                                isSelected: selectedRows.has(requirement.requirementId),
                                onSelect: (checked)=>handleRowSelect(requirement.requirementId, checked),
                                onStatusChange: onStatusChange,
                                isUpdating: updatingRequirementId === requirement.requirementId,
                                onRowClick: onRowClick,
                                onAskAI: onAskAI
                            }, requirement.requirementId))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "bg-gray-50/80 border-t border-gray-200 px-4 py-2",
                children: /*#__PURE__*/ _jsxs("span", {
                    className: "text-xs text-gray-500",
                    children: [
                        sortedRequirements.length,
                        " requirement",
                        sortedRequirements.length !== 1 ? 's' : ''
                    ]
                })
            })
        ]
    });
});
